import React, { Component } from "react";
import {
  withModulesManager,
  formatMessage,
  formatDateFromISO,
} from "@openimis/fe-core";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  Typography,
  Paper,
  Box,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Card,
  CardContent,
  FormHelperText,
  Avatar,
} from "@material-ui/core";
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as BackIcon,
  PhotoCamera,
} from "@material-ui/icons";
import { withStyles } from "@material-ui/core/styles";
import {
  fetchInsureeEnrollment,
  updateInsureeEnrollment,
  submitInsureeEnrollment,
} from "../../actions";
import { toBackendInsureePayload } from "../../utils/enrollmentData";

const styles = (theme) => ({
  page: {
    margin: theme.spacing(1),
  },
  header: {
    marginBottom: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
  title: {
    fontWeight: "bold",
    color: theme.palette.primary.main,
    flex: 1,
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
  },
  form: {
    marginTop: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  formCard: {
    marginBottom: theme.spacing(2),
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  saveButton: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
});

class InsureeEnrollmentEdit extends Component {
  state = {
    enrollment: null,
    errors: {},
    isLoading: false,
  };

  componentDidMount() {
    // Fetch real enrollment using route param (uuid or id)
    const { match, fetchInsureeEnrollment, modulesManager } = this.props;
    const enrollmentId = match?.params?.enrollmentId || match?.params?.uuid;
    if (fetchInsureeEnrollment && enrollmentId) {
      fetchInsureeEnrollment(modulesManager, enrollmentId);
    }
  }

  componentDidUpdate(prevProps) {
    // Sync detail from Redux into local form state when it changes
    if (
      this.props.insureeEnrollment &&
      this.props.insureeEnrollment !== prevProps.insureeEnrollment
    ) {
      const e = this.props.insureeEnrollment;
      const d = this.parseInsureeData(e);
      this.setState({
        enrollment: {
          uuid: e.uuid,
          chfId: d.chfId || "",
          firstName: d.firstName || "",
          lastName: d.lastName || "",
          otherNames: d.otherNames || "",
          dob: this.formatDateInput(d.dob) || "",
          gender: d.gender || "",
          phone: d.phone || "",
          email: d.email || "",
          address: d.address || "",
          status: e.status || "",
          enrollmentDate: this.formatDateInput(e.dateCreated) || "",
          validityFrom: this.formatDateInput(d.validityFrom) || "",
          validityTo: this.formatDateInput(d.validityTo) || "",
          policyNumber: d.policyNumber || "",
          premiumAmount: d.premiumAmount || "",
          coverageAmount: d.coverageAmount || "",
          familySize: d.familySize || "",
          occupation: d.occupation || "",
          maritalStatus: d.maritalStatus || "",
          emergencyContact: d.emergencyContact || "",
          emergencyPhone: d.emergencyPhone || "",
          photo: e.photo || null,
        },
      });
    }
    // If route param changes, refetch
    const prevId =
      prevProps.match?.params?.enrollmentId || prevProps.match?.params?.uuid;
    const currId =
      this.props.match?.params?.enrollmentId || this.props.match?.params?.uuid;
    if (prevId !== currId && currId && this.props.fetchInsureeEnrollment) {
      this.props.fetchInsureeEnrollment(this.props.modulesManager, currId);
    }
  }

  // Parse insureeData payload similar to detail view
  parseInsureeData = (enrollment) => {
    let data = {};
    try {
      const raw = enrollment?.insureeData;
      if (raw && typeof raw === "string") {
        const first = JSON.parse(raw);
        data = typeof first === "string" ? JSON.parse(first) : first;
      } else if (raw && typeof raw === "object") {
        data = raw;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[InsureeEnrollmentEdit] Failed to parse insureeData", e);
    }
    return {
      chfId: data.chfId || data.chf_id || enrollment?.chfId,
      firstName:
        data.firstName ||
        data.first_name ||
        data.given_name ||
        enrollment?.firstName,
      lastName:
        data.lastName || data.last_name || data.surname || enrollment?.lastName,
      otherNames: data.otherNames || data.other_names || enrollment?.otherNames,
      dob: data.dob || data.dateOfBirth || data.birthDate || enrollment?.dob,
      gender:
        data.gender ||
        data.genderCode ||
        data.sex ||
        data.gender_id ||
        enrollment?.gender,
      phone:
        data.phone || data.phoneNumber || data.phoneNo || enrollment?.phone,
      email: data.email || enrollment?.email,
      address: data.current_address || data.address || enrollment?.address,
      validityFrom:
        data.validityFrom || data.validity_from || enrollment?.validityFrom,
      validityTo: data.validityTo || data.validity_to || enrollment?.validityTo,
      policyNumber: enrollment?.policyNumber,
      premiumAmount: enrollment?.premiumAmount,
      coverageAmount: enrollment?.coverageAmount,
      occupation: enrollment?.occupation,
      maritalStatus: enrollment?.maritalStatus,
      emergencyContact: enrollment?.emergencyContact,
      emergencyPhone: enrollment?.emergencyPhone,
      familySize: enrollment?.familySize,
    };
  };

  formatDateInput = (d) => {
    if (!d) return "";
    try {
      // If already YYYY-MM-DD, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
      return new Date(d).toISOString().slice(0, 10);
    } catch (e) {
      return "";
    }
  };

  handleInputChange = (field) => (event) => {
    const value = event.target.value;
    this.setState({
      enrollment: {
        ...this.state.enrollment,
        [field]: value,
      },
      errors: {
        ...this.state.errors,
        [field]: "",
      },
    });
  };

  validateForm = () => {
    const { enrollment } = this.state;
    const errors = {};

    // Required field validations
    if (!enrollment.chfId.trim()) {
      errors.chfId = "CHF ID is required";
    }
    if (!enrollment.firstName.trim()) {
      errors.firstName = "First name is required";
    }
    if (!enrollment.lastName.trim()) {
      errors.lastName = "Last name is required";
    }
    if (!enrollment.dob) {
      errors.dob = "Date of birth is required";
    }
    if (!enrollment.gender) {
      errors.gender = "Gender is required";
    }
    if (!enrollment.enrollmentDate) {
      errors.enrollmentDate = "Enrollment date is required";
    }

    // Email validation
    if (
      enrollment.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(enrollment.email)
    ) {
      errors.email = "Please enter a valid email address";
    }

    // Phone validation
    if (enrollment.phone && !/^\+?[\d\s-()]+$/.test(enrollment.phone)) {
      errors.phone = "Please enter a valid phone number";
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  handleSave = () => {
    if (!this.validateForm()) return;
    const { enrollment } = this.state;
    const uuid = enrollment?.uuid || this.props.insureeEnrollment?.uuid;
    if (!uuid) return;

    this.setState({ isLoading: true });
    const payload = {
      uuid,
      insureeData: toBackendInsureePayload(enrollment),
      notes: enrollment.notes,
    };
    Promise.resolve(
      this.props.updateInsureeEnrollment(
        this.props.modulesManager,
        payload,
        "Update Insuree Enrollment"
      )
    )
      .then(() => {
        this.setState({ isLoading: false });
        this.handleBack();
      })
      .catch(() => this.setState({ isLoading: false }));
  };

  handleCancel = () => {
    // Navigate back without saving
    this.handleBack();
  };

  handleBack = () => {
    const uuid =
      this.state.enrollment?.uuid || this.props.insureeEnrollment?.uuid;
    if (this.props.history && uuid) {
      this.props.history.push(`/enrollment/insuree/${uuid}/detail`);
    } else {
      window.history.back();
    }
  };

  render() {
    const { classes, intl } = this.props;
    const { enrollment, errors, isLoading } = this.state;

    if (!enrollment) {
      return (
        <Box className={classes.page}>
          <Card style={{ margin: 16 }}>
            <CardContent>
              <Typography variant="h6">Loading...</Typography>
            </CardContent>
          </Card>
        </Box>
      );
    }

    return (
      <Box className={classes.page}>
        <Card style={{ margin: 16 }}>
          <CardContent style={{ padding: 24 }}>
            <Box className={classes.header}>
              <Box display="flex" alignItems="center" flex={1}>
                <IconButton
                  className={classes.backButton}
                  onClick={this.handleBack}
                  color="primary"
                >
                  <BackIcon />
                </IconButton>
                <Typography variant="h4" className={classes.title}>
                  {formatMessage(
                    intl,
                    "enrollment",
                    "insureeEnrollment.edit.title"
                  )}
                </Typography>
              </Box>
            </Box>

            <form className={classes.form}>
              <Grid container spacing={3}>
                {/* Personal Information Section */}
                <Grid item xs={12} md={6}>
                  <Card className={classes.formCard}>
                    <CardContent>
                      <Typography variant="h6" className={classes.sectionTitle}>
                        {formatMessage(
                          intl,
                          "enrollment",
                          "insureeEnrollment.edit.personalInfo"
                        )}
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "insureeEnrollment.chfId"
                            )}
                            value={enrollment.chfId}
                            onChange={this.handleInputChange("chfId")}
                            error={!!errors.chfId}
                            helperText={errors.chfId}
                            required
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "insureeEnrollment.edit.firstName"
                            )}
                            value={enrollment.firstName}
                            onChange={this.handleInputChange("firstName")}
                            error={!!errors.firstName}
                            helperText={errors.firstName}
                            required
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "insureeEnrollment.edit.lastName"
                            )}
                            value={enrollment.lastName}
                            onChange={this.handleInputChange("lastName")}
                            error={!!errors.lastName}
                            helperText={errors.lastName}
                            required
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "insureeEnrollment.otherNames"
                            )}
                            value={enrollment.otherNames}
                            onChange={this.handleInputChange("otherNames")}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "insureeEnrollment.dob"
                            )}
                            type="date"
                            value={enrollment.dob}
                            onChange={this.handleInputChange("dob")}
                            error={!!errors.dob}
                            helperText={errors.dob}
                            InputLabelProps={{ shrink: true }}
                            required
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl
                            fullWidth
                            error={!!errors.gender}
                            required
                          >
                            <InputLabel>
                              {formatMessage(
                                intl,
                                "enrollment",
                                "insureeEnrollment.gender"
                              )}
                            </InputLabel>
                            <Select
                              value={enrollment.gender}
                              onChange={this.handleInputChange("gender")}
                            >
                              <MenuItem value="M">
                                {formatMessage(
                                  intl,
                                  "enrollment",
                                  "insureeEnrollment.male"
                                )}
                              </MenuItem>
                              <MenuItem value="F">
                                {formatMessage(
                                  intl,
                                  "enrollment",
                                  "insureeEnrollment.female"
                                )}
                              </MenuItem>
                            </Select>
                            {errors.gender && (
                              <FormHelperText>{errors.gender}</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "insureeEnrollment.phone"
                            )}
                            value={enrollment.phone}
                            onChange={this.handleInputChange("phone")}
                            error={!!errors.phone}
                            helperText={errors.phone}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "insureeEnrollment.edit.email"
                            )}
                            value={enrollment.email}
                            onChange={this.handleInputChange("email")}
                            error={!!errors.email}
                            helperText={errors.email}
                            type="email"
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Box>
                            <Typography variant="body2" gutterBottom>
                              {formatMessage(
                                intl,
                                "enrollment",
                                "insureeEnrollment.edit.photo"
                              )}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={2}>
                              {enrollment.photo && (
                                <Avatar
                                  src={
                                    enrollment.photo.photo || enrollment.photo
                                  }
                                  alt="Insuree Photo"
                                  style={{ width: 80, height: 80 }}
                                />
                              )}
                              <Box>
                                <input
                                  accept="image/*"
                                  style={{ display: "none" }}
                                  id="photo-upload-edit"
                                  type="file"
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      const reader = new FileReader();
                                      reader.onloadend = () => {
                                        this.setState({
                                          enrollment: {
                                            ...this.state.enrollment,
                                            photo: {
                                              photo: reader.result,
                                              date: new Date().toISOString(),
                                              officer: "current_user",
                                            },
                                          },
                                        });
                                      };
                                      reader.readAsDataURL(file);
                                    }
                                  }}
                                />
                                <label htmlFor="photo-upload-edit">
                                  <Button
                                    variant="outlined"
                                    component="span"
                                    startIcon={<PhotoCamera />}
                                    size="small"
                                  >
                                    {enrollment.photo
                                      ? "Change Photo"
                                      : "Upload Photo"}
                                  </Button>
                                </label>
                                {enrollment.photo && (
                                  <Button
                                    variant="text"
                                    size="small"
                                    color="secondary"
                                    onClick={() => {
                                      this.setState({
                                        enrollment: {
                                          ...this.state.enrollment,
                                          photo: null,
                                        },
                                      });
                                    }}
                                    style={{ marginLeft: 8 }}
                                  >
                                    Remove
                                  </Button>
                                )}
                              </Box>
                            </Box>
                            {enrollment.photo?.date && (
                              <Typography
                                variant="caption"
                                display="block"
                                style={{ marginTop: 4 }}
                              >
                                Uploaded:{" "}
                                {new Date(
                                  enrollment.photo.date
                                ).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "insureeEnrollment.edit.address"
                            )}
                            value={enrollment.address}
                            onChange={this.handleInputChange("address")}
                            multiline
                            minRows={2}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Enrollment Information Section */}
                <Grid item xs={12} md={6}>
                  <Card className={classes.formCard}>
                    <CardContent>
                      <Typography variant="h6" className={classes.sectionTitle}>
                        {formatMessage(
                          intl,
                          "enrollment",
                          "insureeEnrollment.edit.enrollmentInfo"
                        )}
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel>
                              {formatMessage(
                                intl,
                                "enrollment",
                                "insureeEnrollment.status"
                              )}
                            </InputLabel>
                            <Select
                              value={enrollment.status}
                              onChange={this.handleInputChange("status")}
                            >
                              <MenuItem value="Active">Active</MenuItem>
                              <MenuItem value="Inactive">Inactive</MenuItem>
                              <MenuItem value="Pending">Pending</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "insureeEnrollment.enrollmentDate"
                            )}
                            type="date"
                            value={enrollment.enrollmentDate}
                            onChange={this.handleInputChange("enrollmentDate")}
                            error={!!errors.enrollmentDate}
                            helperText={errors.enrollmentDate}
                            InputLabelProps={{ shrink: true }}
                            required
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "insureeEnrollment.edit.validityFrom"
                            )}
                            type="date"
                            value={enrollment.validityFrom}
                            onChange={this.handleInputChange("validityFrom")}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "insureeEnrollment.edit.validityTo"
                            )}
                            type="date"
                            value={enrollment.validityTo}
                            onChange={this.handleInputChange("validityTo")}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "insureeEnrollment.edit.policyNumber"
                            )}
                            value={enrollment.policyNumber}
                            onChange={this.handleInputChange("policyNumber")}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "insureeEnrollment.edit.premiumAmount"
                            )}
                            value={enrollment.premiumAmount}
                            onChange={this.handleInputChange("premiumAmount")}
                            type="number"
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "insureeEnrollment.edit.coverageAmount"
                            )}
                            value={enrollment.coverageAmount}
                            onChange={this.handleInputChange("coverageAmount")}
                            type="number"
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  <Card className={classes.formCard}>
                    <CardContent>
                      <Typography variant="h6" className={classes.sectionTitle}>
                        {formatMessage(
                          intl,
                          "enrollment",
                          "insureeEnrollment.edit.additionalInfo"
                        )}
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "insureeEnrollment.edit.occupation"
                            )}
                            value={enrollment.occupation}
                            onChange={this.handleInputChange("occupation")}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel>
                              {formatMessage(
                                intl,
                                "enrollment",
                                "insureeEnrollment.edit.maritalStatus"
                              )}
                            </InputLabel>
                            <Select
                              value={enrollment.maritalStatus}
                              onChange={this.handleInputChange("maritalStatus")}
                            >
                              <MenuItem value="Single">Single</MenuItem>
                              <MenuItem value="Married">Married</MenuItem>
                              <MenuItem value="Divorced">Divorced</MenuItem>
                              <MenuItem value="Widowed">Widowed</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "insureeEnrollment.edit.familySize"
                            )}
                            value={enrollment.familySize}
                            onChange={this.handleInputChange("familySize")}
                            type="number"
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "insureeEnrollment.edit.emergencyContact"
                            )}
                            value={enrollment.emergencyContact}
                            onChange={this.handleInputChange(
                              "emergencyContact"
                            )}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "insureeEnrollment.edit.emergencyPhone"
                            )}
                            value={enrollment.emergencyPhone}
                            onChange={this.handleInputChange("emergencyPhone")}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Box className={classes.buttonContainer}>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={this.handleCancel}
                  disabled={isLoading}
                >
                  {formatMessage(
                    intl,
                    "enrollment",
                    "insureeEnrollment.edit.cancel"
                  )}
                </Button>
                <Button
                  variant="contained"
                  className={classes.saveButton}
                  startIcon={<SaveIcon />}
                  onClick={this.handleSave}
                  disabled={isLoading}
                >
                  {isLoading
                    ? formatMessage(
                        intl,
                        "enrollment",
                        "insureeEnrollment.edit.saving"
                      )
                    : formatMessage(
                        intl,
                        "enrollment",
                        "insureeEnrollment.edit.save"
                      )}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Box>
    );
  }
}

const mapStateToProps = (state) => ({
  insureeEnrollment: state.enrollment?.insureeEnrollmentDetail || null,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchInsureeEnrollment,
      updateInsureeEnrollment,
      submitInsureeEnrollment,
    },
    dispatch
  );

export default withModulesManager(
  withStyles(styles)(
    injectIntl(
      connect(mapStateToProps, mapDispatchToProps)(InsureeEnrollmentEdit)
    )
  )
);
